/**
 * Formats the current date in "Wed, June 5th" format
 * @param locale - Optional locale for day/month names (defaults to 'en')
 * @returns Formatted date string
 */
export const getDefaultDate = (locale: string = 'en'): string => {
  const now = new Date();
  return formatDate(now, false, undefined, locale);
};

/**
 * Gets the ordinal suffix for a day number
 * @param day - The day number
 * @returns The ordinal suffix (st, nd, rd, th)
 */
export const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) {
    return 'th';
  }
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

/**
 * Formats a date in "Wed, June 5th" format
 * @param date - The date to format
 * @param includeTime - Whether to include time in the format
 * @param timeZone - Optional timezone to use
 * @param locale - Optional locale for day/month names
 * @returns Formatted date string
 */
export const formatDate = (date: Date, includeTime: boolean = false, timeZone?: string, locale: string = 'en'): string => {
  // Use Intl.DateTimeFormat to get localized day and month names
  // Use short weekday for English, long weekday for other locales
  const weekdayFormat = locale.startsWith('en') ? 'short' : 'long';
  const dayFormatter = new Intl.DateTimeFormat(locale, { weekday: weekdayFormat });
  const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'long' });
  
  let targetDate = date;
  
  // If timezone is specified, create a new date in that timezone
  if (timeZone) {
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: includeTime ? 'numeric' : undefined,
      minute: includeTime ? 'numeric' : undefined,
      hour12: false
    });
    
    const parts = formatter.formatToParts(date);
    const year = parts.find((p) => {return p.type === 'year'})?.value;
    const month = parts.find((p) => {return p.type === 'month'})?.value;
    const day = parts.find((p) => {return p.type === 'day'})?.value;
    const hour = includeTime ? parts.find((p) => {return p.type === 'hour'})?.value : '0';
    const minute = includeTime ? parts.find((p) => {return p.type === 'minute'})?.value : '0';
    
    targetDate = new Date(parseInt(year!), parseInt(month!) - 1, parseInt(day!), parseInt(hour!), parseInt(minute!));
  }
  
  let dayName = dayFormatter.format(targetDate);
  let monthName = monthFormatter.format(targetDate);
  const day = targetDate.getDate();
  
  // Capitalize the first letter of weekday and month names for better presentation
  if (dayName.length > 0) {
    dayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
  }
  if (monthName.length > 0) {
    monthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
  }
  
  let formattedDate = `${dayName}, ${monthName} ${day}${getOrdinalSuffix(day)}`;
  
  if (includeTime) {
    const hours = targetDate.getHours().toString().padStart(2, '0');
    const minutes = targetDate.getMinutes().toString().padStart(2, '0');
    formattedDate += ` ${hours}:${minutes}`;
  }
  
  return formattedDate;
};

// ===== LOCALIZED VERSIONS =====

/**
 * Interface for localized date formatting options
 */
export interface LocalizedDateOptions {
  locale?: string;
  format?: 'full' | 'long' | 'medium' | 'short' | 'custom';
  includeTime?: boolean;
  timeZone?: string;
}

/**
 * Default date formatting options for different locales
 */
const LOCALE_DATE_FORMATS: Record<string, Intl.DateTimeFormatOptions> = {
  en: {
    weekday: 'short',
    month: 'long',
    day: 'numeric'
  },
  es: {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  },
  pt: {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  },
  de: {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  },
  fr: {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  },
  hi: {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  },
  it: {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  },
  tl: {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  }
};

/**
 * Gets the ordinal suffix for a day number in different locales
 * @param day - The day number
 * @param locale - The locale to use for ordinal formatting
 * @returns The ordinal suffix appropriate for the locale
 */
export const getLocalizedOrdinalSuffix = (day: number, locale: string = 'en'): string => {
  // For English, use the existing logic
  if (locale.startsWith('en')) {
    return getOrdinalSuffix(day);
  }
  
  // For other locales, use Intl.PluralRules to determine the appropriate suffix
  const pluralRules = new Intl.PluralRules(locale, { type: 'ordinal' });
  const plural = pluralRules.select(day);
  
  // This is a simplified approach - in a real implementation, you might want
  // to have locale-specific ordinal suffix mappings
  switch (plural) {
    case 'one':
      return locale.startsWith('es') ? 'º' : 
             locale.startsWith('pt') ? 'º' : 
             locale.startsWith('it') ? 'º' : '';
    case 'two':
      return locale.startsWith('es') ? 'º' : 
             locale.startsWith('pt') ? 'º' : 
             locale.startsWith('it') ? 'º' : '';
    default:
      return locale.startsWith('es') ? 'º' : 
             locale.startsWith('pt') ? 'º' : 
             locale.startsWith('it') ? 'º' : '';
  }
};

/**
 * Formats a date using localized formatting
 * @param date - The date to format
 * @param options - Localization options
 * @returns Localized formatted date string
 */
export const formatLocalizedDate = (
  date: Date, 
  options: LocalizedDateOptions = {}
): string => {
  const {
    locale = 'en',
    format = 'custom',
    includeTime = false,
    timeZone = 'UTC'
  } = options;

  // Use custom format for English to maintain existing behavior
  if (locale && locale.startsWith('en') && format === 'custom') {
    return formatDate(date, includeTime, timeZone, locale);
  }

  // For other locales, use Intl.DateTimeFormat
  const safeLocale = locale || 'en';
  const formatOptions: Intl.DateTimeFormatOptions = {
    ...LOCALE_DATE_FORMATS[safeLocale] || LOCALE_DATE_FORMATS.en,
    timeZone
  };

  if (includeTime) {
    formatOptions.hour = 'numeric';
    formatOptions.minute = 'numeric';
  }

  const formatter = new Intl.DateTimeFormat(safeLocale, formatOptions);
  let formattedDate = formatter.format(date);

  // Add ordinal suffix for locales that support it
  if (safeLocale.startsWith('en')) {
    const day = date.getDate();
    formattedDate = formattedDate.replace(/\d+/, `${day}${getOrdinalSuffix(day)}`);
  } else if (['es', 'pt', 'it'].some((lang) => {return safeLocale.startsWith(lang)})) {
    const day = date.getDate();
    const ordinalSuffix = getLocalizedOrdinalSuffix(day, safeLocale);
    formattedDate = formattedDate.replace(/\d+/, `${day}${ordinalSuffix}`);
  }

  // Capitalize the first letter of each word for better presentation
  formattedDate = formattedDate.replace(/\b\w/g, (char) => {
    return char.toUpperCase();
  });

  return formattedDate;
};

/**
 * Gets the current date formatted according to the specified locale
 * @param options - Localization options
 * @returns Localized formatted date string
 */
export const getLocalizedDefaultDate = (
  options: LocalizedDateOptions = {}
): string => {
  const { locale = 'en' } = options;
  return getDefaultDate(locale);
};

/**
 * Formats a date with time using localized formatting
 * @param date - The date to format
 * @param options - Localization options
 * @returns Localized formatted date and time string
 */
export const formatLocalizedDateTime = (
  date: Date,
  options: LocalizedDateOptions = {}
): string => {
  return formatLocalizedDate(date, { ...options, includeTime: true });
};

/**
 * Gets relative time string (e.g., "2 hours ago", "yesterday")
 * @param date - The date to get relative time for
 * @param locale - The locale to use
 * @returns Relative time string
 */
export const getRelativeTimeString = (
  date: Date,
  locale: string = 'en'
): string => {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (diffInMinutes < 1) {
    return rtf.format(0, 'minute');
  } else if (diffInMinutes < 60) {
    return rtf.format(-diffInMinutes, 'minute');
  } else if (diffInHours < 24) {
    return rtf.format(-diffInHours, 'hour');
  } else if (diffInDays < 7) {
    return rtf.format(-diffInDays, 'day');
  } else {
    return formatLocalizedDate(date, { locale });
  }
}; 
